import { chmodSync } from "node:fs";
import type { LogEntry, LogQuery, LogQueryResult } from "@skaile/workspaces/types";
import { openSqlite, type PortableDatabase, type PortableStatement } from "./sqlite-runtime.js";
import type { LogSink } from "./types.js";

export interface SqliteSinkOptions {
  path: string; // ':memory:' or filesystem path
}

const SCHEMA = `
CREATE TABLE IF NOT EXISTS log_entries (
  id            TEXT PRIMARY KEY,
  timestamp     TEXT NOT NULL,
  session_id    TEXT NOT NULL,
  kind          TEXT NOT NULL,
  subkind       TEXT NOT NULL,
  instance      TEXT,
  level         TEXT NOT NULL,
  message       TEXT NOT NULL,
  data_json     TEXT,
  error_json    TEXT
);
CREATE INDEX IF NOT EXISTS idx_logs_timestamp ON log_entries(timestamp);
CREATE INDEX IF NOT EXISTS idx_logs_kind_level ON log_entries(kind, level);
CREATE INDEX IF NOT EXISTS idx_logs_source ON log_entries(kind, subkind, instance);
`;

interface LogEntryRow {
  id: string;
  timestamp: string;
  session_id: string;
  kind: string;
  subkind: string;
  instance: string | null;
  level: string;
  message: string;
  data_json: string | null;
  error_json: string | null;
}

export class SqliteSink implements LogSink {
  private db: PortableDatabase;
  private insertStmt: PortableStatement;
  private insertMany: (entries: LogEntry[]) => void;
  private closed = false;

  constructor(opts: SqliteSinkOptions) {
    this.db = openSqlite(opts.path);
    if (opts.path !== ":memory:") {
      this.db.exec("PRAGMA journal_mode = WAL");
    }
    this.db.exec(SCHEMA);
    if (opts.path !== ":memory:") {
      // Group-writable so a co-grouped host process can open read-write for retention.
      makeGroupWritable(opts.path);
    }
    this.insertStmt = this.db.prepare(
      `INSERT INTO log_entries (id, timestamp, session_id, kind, subkind, instance, level, message, data_json, error_json)
       VALUES ($id, $timestamp, $session_id, $kind, $subkind, $instance, $level, $message, $data_json, $error_json)`,
    );
    this.insertMany = this.db.transaction((entries: LogEntry[]) => {
      for (const e of entries) this.insertStmt.run(this.toRow(e));
    });
  }

  write(entry: LogEntry): void {
    if (this.closed) return;
    this.insertStmt.run(this.toRow(entry));
  }

  writeBatch(entries: LogEntry[]): void {
    if (this.closed || entries.length === 0) return;
    this.insertMany(entries);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.db.close();
  }

  query(q: LogQuery): LogQueryResult {
    if (this.closed) return { entries: [] };
    const where: string[] = ["session_id = $sessionId"];
    const params: Record<string, unknown> = { $sessionId: q.sessionId };

    if (q.source?.kind) {
      where.push("kind = $kind");
      params.$kind = q.source.kind;
    }
    if (q.source?.subkind) {
      where.push("subkind = $subkind");
      params.$subkind = q.source.subkind;
    }
    if (q.source?.instance) {
      where.push("instance = $instance");
      params.$instance = q.source.instance;
    }
    if (q.levels && q.levels.length > 0) {
      const placeholders = q.levels.map((_, i) => `$level${i}`).join(", ");
      where.push(`level IN (${placeholders})`);
      q.levels.forEach((lvl, i) => {
        params[`$level${i}`] = lvl;
      });
    }
    if (q.since) {
      where.push("timestamp >= $since");
      params.$since = q.since;
    }
    if (q.until) {
      where.push("timestamp < $until");
      params.$until = q.until;
    }
    if (q.search) {
      // Escape SQL LIKE wildcards so a search for "100%" matches the literal string,
      // not "100<anything>". \\ is also escaped because it's the escape character itself.
      const escaped = q.search.replace(/[\\%_]/g, "\\$&");
      where.push("message LIKE $search ESCAPE '\\'");
      params.$search = `%${escaped}%`;
    }
    if (q.cursor) {
      where.push("id < $cursor");
      params.$cursor = q.cursor;
    }

    const limit = Math.min(q.limit ?? 500, 5000);
    params.$limit = limit + 1;

    const sql = `SELECT id, timestamp, session_id, kind, subkind, instance, level, message, data_json, error_json
      FROM log_entries
      WHERE ${where.join(" AND ")}
      ORDER BY id DESC
      LIMIT $limit`;

    const rows = this.db.prepare(sql).all<LogEntryRow>(params);

    const hasMore = rows.length > limit;
    const sliced = hasMore ? rows.slice(0, limit) : rows;
    const entries: LogEntry[] = sliced.map((r) => ({
      id: r.id,
      timestamp: r.timestamp,
      sessionId: r.session_id,
      source: {
        kind: r.kind as LogEntry["source"]["kind"],
        subkind: r.subkind,
        instance: r.instance ?? undefined,
      },
      level: r.level as LogEntry["level"],
      message: r.message,
      data: r.data_json ? JSON.parse(r.data_json) : undefined,
      error: r.error_json ? JSON.parse(r.error_json) : undefined,
    }));

    return {
      entries,
      nextCursor: hasMore ? entries[entries.length - 1].id : undefined,
    };
  }

  // Both bun:sqlite and node:sqlite bind `$name` SQL placeholders from
  // matching `$`-prefixed object keys.
  private toRow(e: LogEntry): Record<string, unknown> {
    return {
      $id: e.id,
      $timestamp: e.timestamp,
      $session_id: e.sessionId,
      $kind: e.source.kind,
      $subkind: e.source.subkind,
      $instance: e.source.instance ?? null,
      $level: e.level,
      $message: e.message,
      $data_json: e.data ? JSON.stringify(e.data) : null,
      $error_json: e.error ? JSON.stringify(e.error) : null,
    };
  }
}

/**
 * Best-effort `chmod 0664` on the db file and its WAL/SHM siblings. Swallows all
 * errors (missing sibling, EPERM when not the owner) so logging never throws.
 */
function makeGroupWritable(dbPath: string): void {
  for (const p of [dbPath, `${dbPath}-wal`, `${dbPath}-shm`]) {
    try {
      chmodSync(p, 0o664);
    } catch {
      // Sibling may not exist yet, or this process may not own the file.
      // Either way, retention degrades cleanly — never break logging.
    }
  }
}
